import gql from "graphql-tag";
import { useLazyQuery } from "react-apollo";
import styled from "styled-components";
import { Grid } from "@mui/material";
import moment from "moment";
import CardHESP from "../CardHESP/CardHESP";
import CardHESPTitle from "../CardHESP/CardHESPTitle";
import { SkeletonRectangle } from "../Loading/Skeletons";
import SingleMetricChart  from "./charts/SingleMetricChart";
import MultiMetricChart  from "./charts/MultiMetricChart";
import { shouldGroupMetrics, mergeMetricsData, MetricCategory } from "./charts/helpers";
import { camelCaseToTitleCase } from "./charts/formatters";
import TimePeriodSelector, { TimePeriod } from "./TimePeriodSelector";
import {useCallback, useEffect, useState} from "react";

export const GET_DEVICE_METRICS = gql`
    query GetDeviceMetrics($deviceId: ID!, $start: String, $end: String) {
        deviceMetrics(deviceId: $deviceId, start: $start, end: $end) {
            category
            metrics {
                name
                data {
                    timestamp
                    value
                }
            }
        }
    }
`;

interface GetDeviceMetrics {
    deviceMetrics: MetricCategory[];
}

interface Props {
    deviceId: string;
}

interface GetDeviceMetricsVariables {
    deviceId: string;
    start?: string;
    end?: string;
}

const POLL_INTERVAL = 30000; // 30 seconds

const ErrorMessage = styled.div`
    color: #d32f2f;
    padding: 20px 0;
    text-align: center;
`;

const NoData = styled.div`
    color: #787878;
    padding: 20px 0;
    text-align: center;
`;

export default function MonitoringDeviceDetail({ deviceId }: Props) {
    const [metricsData, setMetricsData] = useState<MetricCategory[]>([]);
    const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [timePeriod, setTimePeriod] = useState<TimePeriod>({
        start: moment().subtract(1, "hour").toISOString(),
        end: null, // null = live mode with polling
    });

    const [fetchMetrics] = useLazyQuery<GetDeviceMetrics, GetDeviceMetricsVariables>(
        GET_DEVICE_METRICS,
        {
            fetchPolicy: "network-only",
            onCompleted: (data) => {
                if (data?.deviceMetrics) {
                    if (metricsData.length === 0) {
                        setMetricsData(data.deviceMetrics);
                    } else {
                        setMetricsData(mergeMetricsData(metricsData, data.deviceMetrics));
                    }
                    setLastFetchTime(Date.now());
                }
                setInitialLoading(false);
            },
            onError: (err) => {
                setError(err.message);
                setInitialLoading(false);
            },
        },
    );

    const isLiveMode = timePeriod.end === null;

    const handleTimePeriodChange = useCallback((newPeriod: TimePeriod) => {
        setTimePeriod(newPeriod);
        setMetricsData([]);
        setLastFetchTime(null);
        setInitialLoading(true);
    }, []);

    // Initial fetch based on selected time period
    useEffect(() => {
        fetchMetrics({
            variables: {
                deviceId,
                start: timePeriod.start,
                end: timePeriod.end || undefined,
            },
        });
    }, [deviceId, timePeriod, fetchMetrics]);

    // Polling - only in live mode (when end is null)
    useEffect(() => {
        if (!isLiveMode || !lastFetchTime) return;

        const interval = setInterval(() => {
            const startTime = moment(lastFetchTime).toISOString();
            fetchMetrics({
                variables: {
                    deviceId,
                    start: startTime,
                },
            });
        }, POLL_INTERVAL);

        return () => clearInterval(interval);
    }, [deviceId, lastFetchTime, fetchMetrics, isLiveMode]);

    if (error) {
        return <ErrorMessage>Error loading device metrics: {error}</ErrorMessage>;
    }

    if (initialLoading) {
        return (
            <>
                <TimePeriodSelector value={timePeriod} onChange={handleTimePeriodChange} />
                <Grid container spacing={4}>
                    {[1, 2].map((i) => (
                        <Grid item xs={12} key={i}>
                            <SkeletonRectangle width="100%" height="250px" />
                        </Grid>
                    ))}
                </Grid>
            </>
        );
    }

    if (metricsData.length === 0) {
        return (
            <>
                <TimePeriodSelector value={timePeriod} onChange={handleTimePeriodChange} />
                <NoData>No metrics available for this device</NoData>
            </>
        );
    }

    return (
        <>
            <TimePeriodSelector value={timePeriod} onChange={handleTimePeriodChange} />
            <Grid container spacing={4}>
            {metricsData.map((category) => {
                const isGrouped = shouldGroupMetrics(category.category);
                const metrics = category.metrics || [];
                const categoryTitle = camelCaseToTitleCase(category.category);

                if (metrics.length === 0) {
                    return (
                        <Grid item xs={12} key={category.category}>
                            <CardHESP>
                                <CardHESPTitle title={categoryTitle} />
                                <NoData>No metrics in this category</NoData>
                            </CardHESP>
                        </Grid>
                    );
                }

                // Render grouped multi-line chart for RAM/memory/network metrics
                if (isGrouped && metrics.length > 1) {
                    return (
                        <Grid item xs={12} key={category.category}>
                            <CardHESP>
                                <CardHESPTitle title={categoryTitle} />
                                <MultiMetricChart
                                    metrics={metrics}
                                    categoryHint={category.category}
                                />
                            </CardHESP>
                        </Grid>
                    );
                }

                // TODO might go away when data is available
                // Render individual charts for non-grouped metrics
                return (
                    <Grid item xs={12} key={category.category}>
                        <CardHESP>
                            <CardHESPTitle title={categoryTitle} />
                            <Grid container spacing={3} style={{ marginTop: "8px" }}>
                                {metrics.map((metric) => (
                                    <Grid item xs={12} md={6} key={metric.name}>
                                        <SingleMetricChart
                                            name={metric.name}
                                            data={metric.data}
                                            categoryHint={category.category}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </CardHESP>
                    </Grid>
                );
            })}
            </Grid>
        </>
    );
}
